/**
 * @description Renders the sync file field
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { FilePond } from 'react-filepond';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

import CustomFormLabel from '@seccon/shared/ui/components/CustomFormLabel';

const SyncFileField = ({ onFileAdd, setExistingPersonnel }) => {
  const { control, setError, clearErrors } = useFormContext();
  const syncMap = useWatch({ name: 'syncMap' });
  const syncOnSsn = useWatch({ name: 'syncOnSsn' });

  // The sync file field should be hidden until the map is selected
  if (!syncMap) {
    return null;
  }

  return (
    <Controller
      name='syncFile'
      control={control}
      render={({ field: { onChange, value }, fieldState }) => {
        const { error } = fieldState;

        /**
         * Handles change events to the file selector by saving the file to the form state
         * @param fileItems {File[]} - file items passed to the handler by FilePond
         */
        function handleChange (fileItems) {
          onChange(
            fileItems.map((entry) => entry.file)
          )

          // If the file was cleared from the field, clear any errors too
          if (fileItems.length === 0) {
            clearErrors('syncFile');
            setExistingPersonnel(null)
          }
        }

        return (
          <FormControl fullWidth error={Boolean(error)} required>
            <CustomFormLabel label='Personnel File to Sync' p={0}>
              <FilePond
                acceptedFileTypes={[
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                ]}
                allowMultiple={false}
                credits={false}
                files={value}
                fileValidateTypeDetectType={(source, type) => {
                  // Any file with a xlsx extension should be normalized to the spreadsheet mime type
                  if (/.xlsx$/.test(source.name)) {
                    return Promise.resolve('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                  }

                  return Promise.resolve(type);
                }}
                fileValidateTypeLabelExpectedTypes='Expects .xlsx'
                labelIdle='Drag & Drop the File or <span class="filepond--label-action">Browse</span>'
                onerror={(err) => setError('syncFile', { type: 'custom', message: Object.values(err).join('. ') })}
                onupdatefiles={(fileItems) => handleChange(fileItems)}
                onaddfile={(e, file) => onFileAdd([file.file], syncMap.name, syncOnSsn)}
              />
            </CustomFormLabel>
            {error && (
              <FormHelperText id='syncFile-text'>
                {error.message}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
      rules={{
        required: 'Personnel Sync file is required'
      }}
    />
  )
};

SyncFileField.propTypes = {
  onFileAdd: PropTypes.func.isRequired,
  setExistingPersonnel: PropTypes.func.isRequired
}

export default SyncFileField;
